import { Card, CardActionArea, CardContent, Typography } from '@material-ui/core';
import { BarChart } from '@material-ui/icons';
import * as React from 'react';
import { useAgent } from '../../hooks/use-agent';

import { usePageDialog } from '../../hooks/use-page-dialog';
import { AgentResultsChart } from './agent-results-chart';
import { AgentResultsErrorBoundary } from './agent-results-error-boundary';
import { ChartData } from './agent-types';

interface ChartFeedPreviewProps {
    chart: ChartData;
}

export const ChartFeedPreview: React.FC<ChartFeedPreviewProps> = ({ chart }) => {
    const { showDialog } = usePageDialog();
    const { conversationId } = useAgent();

    const handleClick = () => {
        showDialog(
            <AgentResultsErrorBoundary>
                <AgentResultsChart chart={chart} />
            </AgentResultsErrorBoundary>,
            `/orbit/chat/${conversationId}`,
            'replace'
        );
    };

    const chartTypeLabel = formatChartType(chart.type);
    const totalDataPoints = chart.series?.reduce((sum, series) => sum + (series?.data?.length || 0), 0) || 0;
    const seriesCount = chart.series?.length || 0;
    const seriesCountText = `${seriesCount} series`;
    const dataPointsText = `${totalDataPoints} data point${totalDataPoints !== 1 ? 's' : ''}`;

    return (
        <Card variant="outlined" style={{ marginTop: 8, marginBottom: 8 }}>
            <CardActionArea onClick={handleClick}>
                <CardContent style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <BarChart style={{ fontSize: 32, color: '#1976d2' }} />
                    <div style={{ flex: 1 }}>
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                            {chart.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {chartTypeLabel} • {seriesCountText} • {dataPointsText}
                        </Typography>
                    </div>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

function formatChartType(type: string): string {
    switch (type) {
        case 'line':
            return 'Line chart';
        case 'bar':
            return 'Bar chart';
        case 'horizontal-bar':
            return 'Horizontal bar chart';
        case 'pie':
            return 'Pie chart';
        case 'donut':
            return 'Donut chart';
        case 'area':
            return 'Area chart';
        default:
            return 'Chart';
    }
}
